/**
 * Customer (guest) API: wire types and calls, exactly as the backend implements them
 * (services/core-api: catalog, cart, order, billing and assistance modules). Amounts are integer minor
 * units. Nothing here calculates; every figure shown to a customer is one of these fields.
 */
import { guestApi } from '@/api/instances';
import type { ApiClient } from '@/api/client';

// ─── Menu: GET /guest/menu ───────────────────────────────────────────────────

export interface ModifierOption {
  id: string;
  name: string;
  price_delta: number;
}

export interface ModifierGroup {
  id: string;
  name: string;
  min_selection: number;
  max_selection: number;
  options: ModifierOption[];
}

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  base_price: number;
  /** The server's answer: an unavailable item cannot be added or ordered, whatever this screen shows. */
  is_available: boolean;
  /** "Today's special" for this branch. Presentation only. */
  is_special: boolean;
  /** Site-relative path of the dish picture, or null. */
  image_url: string | null;
  tax_rate_basis_points: number;
  modifier_groups: ModifierGroup[];
}

export interface MenuCategory {
  id: string;
  name: string;
  sort_order: number;
  items: MenuItem[];
}

export interface GuestMenu {
  categories: MenuCategory[];
}

// ─── Shared session cart: /guest/cart ────────────────────────────────────────

export type CartAddedBy = 'CUSTOMER' | 'WAITER';
export type CartLineIssue = 'ITEM_UNAVAILABLE' | 'INVALID_MODIFIERS';

export interface CartLine {
  cart_item_id: string;
  menu_item_id: string;
  name: string | null;
  quantity: number;
  notes: string | null;
  modifiers: { modifier_option_id: string; name: string; price_delta: number }[];
  unit_amount: number | null;
  line_amount: number | null;
  is_available: boolean;
  issue: CartLineIssue | null;
  added_by: CartAddedBy;
  added_at: string;
}

export interface Cart {
  session_id: string;
  revision: number;
  updated_at: string | null;
  item_count: number;
  total_quantity: number;
  is_valid: boolean;
  subtotal_amount: number;
  tax_amount: number;
  total_amount: number;
  /** Not sent by the backend today. Shown when a later backend version supplies it (PFD-02). */
  service_charge_amount?: number | null;
  items: CartLine[];
}

export interface AddCartItemInput {
  menu_item_id: string;
  quantity: number;
  modifiers: { modifier_option_id: string }[];
  notes?: string;
}

// ─── Orders: /guest/orders ───────────────────────────────────────────────────

export interface PlacedOrder {
  order_id: string;
  order_number: string;
  session_id: string;
  status: string;
  subtotal_amount: number;
  tax_amount: number;
  total_amount: number;
  created_at: string;
}

export interface OrderItem {
  order_item_id: string;
  name: string;
  quantity: number;
  fulfillment_status: string;
  modifiers: string[];
}

export interface Order {
  order_id: string;
  order_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  items: OrderItem[];
}

export interface CancelledOrder {
  order_id: string;
  order_number: string;
  status: string;
  cancelled_at: string;
}

// ─── Bill: GET /guest/bill ───────────────────────────────────────────────────

export interface Bill {
  bill_id: string;
  session_id: string;
  subtotal_amount: number;
  tax_amount: number;
  service_charge_amount: number;
  discount_amount: number;
  total_amount: number;
  paid_amount: number;
  remaining_balance: number;
  settlement_status: string;
  items: {
    order_number: string;
    order_item_id: string;
    name: string;
    quantity: number;
    unit_amount: number;
    line_amount: number;
    modifiers: string[];
  }[];
  adjustments: {
    adjustment_id: string;
    adjustment_type: string;
    amount: number;
    reason_code: string | null;
    reason: string;
    created_at: string;
  }[];
  payments: {
    payment_id: string;
    tender_type: string;
    amount: number;
    status: string;
    created_at: string;
  }[];
}

// ─── Assistance: POST /guest/assistance ──────────────────────────────────────

export const ASSISTANCE_TYPES = ['CALL_WAITER', 'WATER', 'CUTLERY', 'BILL', 'OTHER'] as const;
export type AssistanceType = (typeof ASSISTANCE_TYPES)[number];

export interface AssistanceRequest {
  request_id: string;
  type: string;
  note: string | null;
  status: string;
  created_at: string;
  resolved_at: string | null;
}

export interface AssistanceOutcome {
  request: AssistanceRequest;
  /** False when the server returned a request that was already active (it does not create a second one). */
  created: boolean;
}

// ─── Calls ───────────────────────────────────────────────────────────────────

export function createCustomerApi(api: ApiClient = guestApi) {
  return {
    getMenu: (signal?: AbortSignal) => api.get<GuestMenu>('/guest/menu', { signal }),

    getCart: (signal?: AbortSignal) => api.get<Cart>('/guest/cart', { signal }),

    addCartItem: (input: AddCartItemInput, idempotencyKey: string) =>
      api.post<Cart>('/guest/cart/items', input, { idempotencyKey }),

    updateCartItem: (cartItemId: string, quantity: number) =>
      api.patch<Cart>(`/guest/cart/items/${cartItemId}`, { quantity }),

    removeCartItem: (cartItemId: string) => api.delete<Cart>(`/guest/cart/items/${cartItemId}`),

    /** The only way the shared cart becomes an order. `expectedRevision` is the revision the customer reviewed. */
    placeCartOrder: (expectedRevision: number, idempotencyKey: string) =>
      api.post<PlacedOrder>(
        '/guest/cart/order',
        { expected_revision: expectedRevision },
        { idempotencyKey },
      ),

    getOrders: (signal?: AbortSignal) => api.get<Order[]>('/guest/orders', { signal }),

    cancelOrder: (orderId: string) => api.post<CancelledOrder>(`/guest/orders/${orderId}/cancel`),

    getBill: (signal?: AbortSignal) => api.get<Bill>('/guest/bill', { signal }),

    async requestAssistance(type: AssistanceType, note?: string): Promise<AssistanceOutcome> {
      const result = await api.send<AssistanceRequest>('/guest/assistance', {
        method: 'POST',
        body: note ? { type, note } : { type },
      });
      return { request: result.data, created: result.status === 201 };
    },
  };
}

export type CustomerApi = ReturnType<typeof createCustomerApi>;
export const customerApi = createCustomerApi();
